import { Chip, SxProps, Theme, Typography, styled } from '@mui/material'
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded'
import { useMemo } from 'react'
import { colorPalette } from '../../../../style/colorPalette'
import {
  TextMatchCondition,
  TextMatchMode,
  TextMatchQuantifier,
} from '../../../../../domain/value-object/WbsItemSearchConditionV2Vo'
import { intl } from '../../../../../i18n'

const chipStyle: Record<
  TextMatchMode,
  { background: string; color: string; border: string }
> = {
  include: {
    background: colorPalette.skyBlue[1],
    color: colorPalette.skyBlue[8],
    border: colorPalette.skyBlue[7],
  },
  exclude: {
    background: colorPalette.pink[0],
    color: colorPalette.pink[6],
    border: colorPalette.pink[5],
  },
}

const quantifierLabelStyle: SxProps<Theme> = {
  fontSize: '10px',
  color: colorPalette.monotone[4],
  fontWeight: 600,
  lineHeight: 1,
  flexShrink: 0,
}

const groupLabelStyle = (mode: TextMatchMode): SxProps<Theme> => ({
  fontSize: '10px',
  color: mode === 'include' ? colorPalette.skyBlue[7] : colorPalette.pink[5],
  fontWeight: 600,
  lineHeight: 1,
  flexShrink: 0,
})

const ChipRow = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '4px',
})

const ToggleButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2px',
  cursor: 'pointer',
  border: 'none',
  background: 'none',
  padding: 0,
  borderRadius: '4px',
  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
  },
})

type IndexedCondition = {
  condition: TextMatchCondition
  originalIndex: number
}

type QuantifierLabelIds = {
  anyQuantifier: string
  allQuantifier: string
  includeMode: string
  excludeMode: string
}

const defaultQuantifierLabelIds: QuantifierLabelIds = {
  anyQuantifier: 'wbsItemSearch.quantifier.any',
  allQuantifier: 'wbsItemSearch.quantifier.all',
  includeMode: 'wbsItemSearch.quantifier.include',
  excludeMode: 'wbsItemSearch.quantifier.exclude',
}

type Props = {
  value: TextMatchCondition[] | undefined
  onToggle: (index: number) => void
  onDelete: (index: number) => void
  getLabel?: (condition: TextMatchCondition) => string
  includeQuantifier?: TextMatchQuantifier
  excludeQuantifier?: TextMatchQuantifier
  onToggleIncludeQuantifier?: () => void
  onToggleExcludeQuantifier?: () => void
  labelIds?: QuantifierLabelIds
  sx?: SxProps<Theme>
}

const GroupedChipList = ({
  value,
  onToggle,
  onDelete,
  getLabel,
  includeQuantifier,
  excludeQuantifier,
  onToggleIncludeQuantifier,
  onToggleExcludeQuantifier,
  labelIds = defaultQuantifierLabelIds,
  sx,
}: Props) => {
  const { includeChips, excludeChips } = useMemo(() => {
    if (!value || value.length === 0) {
      return { includeChips: [], excludeChips: [] }
    }
    const inc: IndexedCondition[] = []
    const exc: IndexedCondition[] = []
    value.forEach((c, i) => {
      ;(c.mode === 'exclude' ? exc : inc).push({
        condition: c,
        originalIndex: i,
      })
    })
    return { includeChips: inc, excludeChips: exc }
  }, [value])

  const chipLabel = (c: TextMatchCondition) =>
    getLabel ? getLabel(c) : c.value

  const renderChip = ({ condition, originalIndex }: IndexedCondition) => {
    const s = chipStyle[condition.mode]
    return (
      <Chip
        key={originalIndex}
        label={chipLabel(condition)}
        size="small"
        onClick={() => onToggle(originalIndex)}
        onDelete={() => onDelete(originalIndex)}
        sx={{
          backgroundColor: s.background,
          color: s.color,
          border: `1px solid ${s.border}`,
          cursor: 'pointer',
          textDecoration: 'none',
          '& .MuiChip-deleteIcon': {
            color: s.color,
            '&:hover': { color: s.border },
          },
        }}
      />
    )
  }

  const quantifierLabel = (
    quantifier: TextMatchQuantifier | undefined,
    defaultQuantifier: TextMatchQuantifier = 'any'
  ) =>
    intl.formatMessage({
      id:
        (quantifier ?? defaultQuantifier) === 'all'
          ? labelIds.allQuantifier
          : labelIds.anyQuantifier,
    })

  if (includeChips.length === 0 && excludeChips.length === 0) {
    return null
  }

  return (
    <ChipListContainer sx={sx}>
      {includeChips.length > 0 && (
        <ChipRow>
          {onToggleIncludeQuantifier ? (
            <ToggleButton onClick={onToggleIncludeQuantifier}>
              <SwapHorizRoundedIcon
                sx={{
                  fontSize: '14px',
                  color: colorPalette.monotone[4],
                }}
              />
              <Typography sx={quantifierLabelStyle}>
                {quantifierLabel(includeQuantifier)}
              </Typography>
            </ToggleButton>
          ) : (
            <Typography sx={quantifierLabelStyle}>
              {quantifierLabel(includeQuantifier)}
            </Typography>
          )}
          <Typography sx={groupLabelStyle('include')}>
            {intl.formatMessage({ id: labelIds.includeMode })}
          </Typography>
          {includeChips.map(item => renderChip(item))}
        </ChipRow>
      )}
      {excludeChips.length > 0 && (
        <ChipRow>
          {onToggleExcludeQuantifier ? (
            <ToggleButton onClick={onToggleExcludeQuantifier}>
              <SwapHorizRoundedIcon
                sx={{
                  fontSize: '14px',
                  color: colorPalette.monotone[4],
                }}
              />
              <Typography sx={quantifierLabelStyle}>
                {quantifierLabel(excludeQuantifier, 'all')}
              </Typography>
            </ToggleButton>
          ) : (
            <Typography sx={quantifierLabelStyle}>
              {quantifierLabel(excludeQuantifier, 'all')}
            </Typography>
          )}
          <Typography sx={groupLabelStyle('exclude')}>
            {intl.formatMessage({ id: labelIds.excludeMode })}
          </Typography>
          {excludeChips.map(item => renderChip(item))}
        </ChipRow>
      )}
    </ChipListContainer>
  )
}

const ChipListContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
})

export default GroupedChipList
